import LoadingModal from "components/common/loading/LoadingModal";
import React, { useCallback, useEffect } from "react";
import "./Documents.css";
import DocumentModal from "./modal/DocumentModal";

import { useTheme } from "@mui/material";
import DownloadLetterTemplateModal from "./modal/DownloadLetterTemplateModal";
import PreviewModal from "./modal/PreviewModal";

import LoadingSpinner from "components/LoadingSpinner";
import useDocuments from "components/pages/transmissions/modal/patient/tabs/generalInformation/tabs/documents/useDocuments";
import BatteryInformationModal from "./modal/BatteryInformationModal";

import useDocumentsTableColumn from "components/pages/transmissions/modal/patient/tabs/generalInformation/tabs/documents/columns/useDocumentsTableColumn";
import DocumentsTitle from "components/pages/transmissions/modal/patient/tabs/generalInformation/tabs/documents/title/DocumentsTitle";
import { useSelector } from "react-redux";

import {
  setIsSendingSuccess,
  setIsUploadModalOpen,
  setLetterNextSendDateModal,
  setShowBatteryInformationModal,
  setShowDownloadTemplate,
  setShowNoShowEmailModal,
  setShowPreview,
} from "app/store/patientProfile/documents/slice/patientProfileDocumentSlice";
import CustomTable from "components/common/table/CustomTable";
import { MRT_GlobalFilterTextField as GlobalTextField } from "material-react-table";
import { USER_ROLES } from "static/constants";
import NoShowEmailModal from "./modal/NoShowEmailModal";
import LetterNextSendsModal from "./modal/LetterNextSendsModal";

const Documents = () => {
  const theme = useTheme();

  const {
    information,
    tableData,
    processNoShowEmail,

    dispatch,
    fetchTableData,
    handleFileUpload,
    handleDownloadLetter,
    handleBatteryInformation,
    handleSaveLetterTemplate,
    handleLetterNextSendDate,
  } = useDocuments();

  const {
    file,
    isLoading,
    isDocumentTableLoading,
    isDownloading,
    isFileLoading,
    isUploadModalOpen,
    showDownloadTemplate,
    showBatteryInformationModal,
    // setLetterNextSendDateModal,
    showPreview,
    noShowPreview,
    isSendingLetter,
    isSendingSuccess,
    showPreviewMode,
    batteryInformation,
    downloadingModalText,
    showNoShowEmailModal,
    selectedTemplate,
    letterNextSendDate,
    showLetterNextSendDateModal,
  } = useSelector((state) => state.patientProfileDocument);

  const { currentUser } = useSelector((state) => state.auth);

  useEffect(() => {
    fetchTableData();
    if (isSendingSuccess) dispatch(setIsSendingSuccess(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSendingSuccess]);

  const { documentsTableColumn } = useDocumentsTableColumn();

  const handlePrint = useCallback(() => {
    const iframe = document.createElement("iframe");
    iframe.style.display = "none";
    iframe.src = file?.url;
    iframe.onload = () => {
      iframe.contentWindow.print();
    };
    document.body.appendChild(iframe);
  }, [file]);

  const getLetterActionButtons = () => {
    const actionButtons = [
      {
        label: "Print",
        variant: "contained",
        color: "inherit",
        onClick: handlePrint,
      },
      {
        label: "Download",
        variant: "contained",
        color: "info",
        onClick: handleDownloadLetter,
      },
    ];

    if (!showPreviewMode) {
      const saveButton = {
        label: "Save",
        variant: "contained",
        color: "secondary",
        onClick: handleSaveLetterTemplate,
      };

      actionButtons.push(saveButton);
    }

    return actionButtons;
  };

  const handleSendEmailClick = () => {
    dispatch(setShowNoShowEmailModal(true));
  };

  const getNoShowLetterActionButtons = () => {
    const actionButtons = [
      {
        label: "Send Email",
        variant: "contained",
        color: "secondary",
        onClick: handleSendEmailClick,
      },
    ];

    return actionButtons;
  };

  const validRoles = [
    USER_ROLES.USER,
    USER_ROLES.ADMIN,
    USER_ROLES.ASSISTANT,
    USER_ROLES.CLINIC_ADMIN,
  ];

  const PAGE_SIZE = 5;

  return (
    <>
      {isDocumentTableLoading ? (
        <div className="patient-modal__section">
          <div
            className="upload-table__loading"
            style={{ backgroundColor: theme.palette.background.paper }}
          >
            <LoadingSpinner />
          </div>
        </div>
      ) : (
        <CustomTable
          options={{
            pageSize: PAGE_SIZE,
            enableTopToolbar: true,
            enableSorting: false,
            state: {
              isLoading: isDocumentTableLoading,
              showGlobalFilter: true,
            },
            rowsPerPageOptions: [5, 10],
            renderTopToolbar: ({ table }) => {
              const hasValidRoles = validRoles.includes(currentUser.role);
              return (
                <div
                  className={`d-flex justify-between align-items-center ml-2 mr-2 g-1 ${
                    !hasValidRoles ? "mt-2" : ""
                  }`}
                >
                  {hasValidRoles ? (
                    <DocumentsTitle />
                  ) : (
                    <div className="mt-2" style={{ flexGrow: 1 }}></div>
                  )}

                  <div className="align-items-center">
                    <GlobalTextField table={table} />
                  </div>
                </div>
              );
            },
            muiColumnActionsButtonProps: {
              sx: {
                alignItems: "center !important",
              },
            },
          }}
          columns={documentsTableColumn}
          data={tableData ?? []}
        />
      )}

      {isUploadModalOpen && (
        <DocumentModal
          isModalOpen={isUploadModalOpen}
          onClose={() => dispatch(setIsUploadModalOpen(false))}
          onFileUpload={handleFileUpload}
          isLoading={isLoading}
        />
      )}

      {isDownloading && (
        <LoadingModal open={isDownloading} loadingText={downloadingModalText} />
      )}
      {isSendingLetter && (
        <LoadingModal
          open={isSendingLetter}
          loadingText={downloadingModalText}
        />
      )}
      {showPreview && (
        <PreviewModal
          isLoading={isFileLoading}
          isModalOpen={showPreview}
          onClose={() => dispatch(setShowPreview(false))}
          isPreviewMode={showPreviewMode}
          actionButtons={
            noShowPreview
              ? getNoShowLetterActionButtons()
              : getLetterActionButtons()
          }
        />
      )}
      {showDownloadTemplate && (
        <DownloadLetterTemplateModal
          isModalOpen={showDownloadTemplate}
          onClose={() => dispatch(setShowDownloadTemplate(false))}
          onDownload={handleDownloadLetter}
        />
      )}
      {showBatteryInformationModal && (
        <BatteryInformationModal
          isModalOpen={showBatteryInformationModal}
          onClose={() => dispatch(setShowBatteryInformationModal(false))}
          onDownload={handleBatteryInformation}
          batteryInformation={batteryInformation}
        />
      )}

      {showLetterNextSendDateModal && (
        <LetterNextSendsModal
          isModalOpen={showLetterNextSendDateModal}
          onClose={() => dispatch(setLetterNextSendDateModal(false))}
          onDownload={handleLetterNextSendDate}
          letterNextSendDate={letterNextSendDate}
        />
      )}

      {showNoShowEmailModal && (
        <NoShowEmailModal
          isModalOpen={showNoShowEmailModal}
          onClose={() => {
            dispatch(setShowNoShowEmailModal(false));
          }}
          patientEmail={information?.email}
          isDateRequired={selectedTemplate?.is_date_required}
          onSubmit={processNoShowEmail}
        />
      )}
    </>
  );
};

export default Documents;
